import React, { useState } from 'react';
import { clientsAPI } from '../../services/api';
import { ModernInput, ModernButton, ModernSelect } from '../ui/ModernComponents';
import { User, UserCheck, Phone, MapPin, Save, X, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const referredByOptions = [
  { value: 'google', label: 'Google' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'voisin', label: 'Voisin' },
  { value: 'passager', label: 'Passager' },
  { value: 'ancien client', label: 'Ancien client' },
  { value: 'reco ancien client', label: 'Reco ancien client' },
  { value: 'reco medecin', label: 'Reco medecin' },
  { value: 'ami abdou', label: 'Ami abdou' },
  { value: 'chat gpt', label: 'Chat GPT' },
  { value: 'bni', label: 'BNI' },
  { value: 'convention', label: 'Convention' },
  { value: 'autre', label: 'Autre' },
];

const defaultForm = {
  name: '',
  contact: '',
  phone: '',
  address: '',
  referred_by: '',
};

function AddClientForm({ onSuccess, onCancel }) {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const { user, canCreateData } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSelectChange = (selectedOption, name) => {
    setForm(f => ({ ...f, [name]: selectedOption ? selectedOption.value : '' }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Check user permissions
    console.log('=== CLIENT CREATION DEBUG ===');
    console.log('Current user:', user);
    console.log('Can create data:', canCreateData());
    console.log('User role:', user?.role);
    console.log('Form data:', form);
    console.log('Form validation:', {
      name: form.name,
      nameValid: form.name && form.name.trim() !== '',
      contact: form.contact,
      phone: form.phone,
      address: form.address,
      referred_by: form.referred_by
    });
    
    // Validate form data
    if (!form.name || form.name.trim() === '') {
      toast.error('Le nom du client est requis');
      setLoading(false);
      return;
    }
    
    if (!canCreateData()) {
      toast.error('Vous n\'avez pas les permissions nécessaires pour créer un client.');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Submitting client form:', form);
      console.log('API call to:', '/clients');
      console.log('Request payload:', {
        name: form.name,
        contact: form.contact || '',
        phone: form.phone || '',
        address: form.address || '',
        referred_by: form.referred_by || ''
      });
      
      const res = await clientsAPI.createClient(form);
      console.log('Client creation response:', res);
      console.log('Response data:', res.data);
      console.log('Response status:', res.status);
      
      toast.success('Client ajouté avec succès');
      setForm(defaultForm);
      
      // Handle the new response format
      if (onSuccess) {
        console.log('Calling onSuccess with:', res.data);
        onSuccess(res.data);
      }
    } catch (err) {
      console.error('Client creation error:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      let errorMessage = 'Erreur lors de la sauvegarde';
      
      if (err.response) {
        const { status, data } = err.response;
        console.error('Response status:', status);
        console.error('Response data:', data);
        
        if (status === 403) {
          errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
        } else if (status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (status === 400 && data.errors) {
          errorMessage = data.errors.map(e => e.msg).join(', ');
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 no-shake">
      <div className="space-y-4">
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Nom du client</label>
          <ModernInput
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Ex: Jean Dupont"
            icon={<User className="w-5 h-5" />}
            required
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Contact</label>
          <ModernInput
            name="contact"
            value={form.contact}
            onChange={handleChange}
            placeholder="Ex: Marie Dupont"
            icon={<UserCheck className="w-5 h-5" />}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Téléphone</label>
          <ModernInput
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Ex: +33 1 23 45 67 89"
            icon={<Phone className="w-5 h-5" />}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">Adresse</label>
          <ModernInput
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Ex: 123 Rue de la Paix, Paris"
            icon={<MapPin className="w-5 h-5" />}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-base font-semibold text-gray-800 mb-1">De la part de qui</label>
          <ModernSelect
            name="referred_by"
            options={referredByOptions}
            value={form.referred_by ? referredByOptions.find(opt => opt.value === form.referred_by) : null}
            onChange={(selectedOption) => handleSelectChange(selectedOption, 'referred_by')}
            placeholder="Sélectionner une option"
            isClearable
          />
        </div>
      </div>
      <div className="flex justify-end space-x-2 pt-2">
        <ModernButton type="button" variant="secondary" onClick={onCancel} icon={<X className="w-4 h-4" />}>
          Annuler
        </ModernButton>
        <ModernButton type="button" variant="primary" icon={<Save className="w-4 h-4" />} disabled={loading} onClick={handleSubmit}>
          {loading ? 'Ajout...' : 'Ajouter'}
        </ModernButton>
      </div>
    </div>
  );
}

export default AddClientForm; 